import React, { useState, useEffect } from 'react';
import { Modal, View, TouchableWithoutFeedback, Platform, StyleSheet, Dimensions } from 'react-native';
import DateTimePicker, { useDefaultStyles } from 'react-native-ui-datepicker';
import NativeDateTimePicker from '@react-native-community/datetimepicker';
import { TextInput, useTheme } from 'react-native-paper';

const { width: screenWidth } = Dimensions.get('window');

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateToString = (date: Date | undefined | null): string => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

export const CustomDatePicker = ({
  label,
  value,
  onChange,
  error,
  maxDate,
  disabled,
  testID,
}: {
  label: string;
  value: string;
  onChange: (date: string) => void;
  error?: boolean;
  maxDate?: string;
  disabled?: boolean;
  testID?: string;
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [nativeDatePickerShow, setNativeDatePickerShow] = useState(false);
  const [inputValue, setInputValue] = useState(formatDate(value));
  const theme = useTheme();
  const isDarkMode = theme.dark;
  const defaultStyles = useDefaultStyles();

  // Theme colors for native pickers
  const androidAccentColor = theme.colors.primary;
  const androidBackgroundColor = isDarkMode ? '#333333' : '#FFFFFF';
  const androidHeaderBackgroundColor = theme.colors.primary;
  const androidHeaderTextColor = '#FFFFFF';
  const androidTextColor = isDarkMode ? '#FFFFFF' : '#000000';

  const themedStyles = {
    ...defaultStyles,
    day: {
      ...defaultStyles.day,
      backgroundColor: isDarkMode ? '#444' : '#f5f5f5'
    },
    today: {
      ...defaultStyles.today,
      borderColor: theme.colors.primary
    },
    selected: {
      ...defaultStyles.selected,
      backgroundColor: theme.colors.primary
    },
    selected_label: {
      ...defaultStyles.selected_label,
      color: '#fff'
    }
  };

  const dateValue = value ? new Date(value) : undefined;

  useEffect(() => {
    setInputValue(formatDate(value));
  }, [value]);

  const onNativeDateChange = (event: any, selectedDate?: Date) => {
    setNativeDatePickerShow(false);
    if (event.type === 'dismissed') return;
    if (selectedDate) {
      onChange(formatDateToString(selectedDate));
    }
  };

  const parseUserInputDate = (input: string): Date | null => {
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = input.match(dateRegex);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const date = new Date(year, month, day);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day &&
      (!maxDate || date <= new Date(maxDate))
    ) {
      return date;
    }
    return null;
  };

  const handleTextInputChange = (text: string) => {
    const numericText = text.replace(/[^0-9]/g, '');
    let formattedText = '';
    if (numericText.length <= 2) {
      formattedText = numericText;
    } else if (numericText.length <= 4) {
      formattedText = `${numericText.slice(0, 2)}/${numericText.slice(2)}`;
    } else {
      formattedText = `${numericText.slice(0, 2)}/${numericText.slice(2, 4)}/${numericText.slice(4, 8)}`;
    }
    setInputValue(formattedText);
    // Commit ISO as soon as DD/MM/YYYY is complete so form state does not depend
    // on blur (Continue can be pressed before blur on web).
    if (numericText.length === 8) {
      const parsedDate = parseUserInputDate(formattedText);
      if (parsedDate) {
        onChange(formatDateToString(parsedDate));
      }
    }
  };

  const handleInputBlur = () => {
    const parsedDate = parseUserInputDate(inputValue);
    if (parsedDate) {
      onChange(formatDateToString(parsedDate));
    } else {
      setInputValue(formatDate(value));
    }
  };

  return (
    <>
      <TextInput
        label={label}
        mode="outlined"
        value={inputValue}
        style={{ marginBottom: 10 }}
        editable={!disabled}
        disabled={disabled}
        error={!!error}
        onChangeText={handleTextInputChange}
        onBlur={handleInputBlur}
        placeholder="DD/MM/YYYY"
        keyboardType="numeric"
        maxLength={10}
        testID={testID}
        right={
          <TextInput.Icon
            icon="calendar"
            disabled={disabled}
            onPress={() => {
              if (disabled) return;
              if (Platform.OS === 'web') {
                setModalVisible(true);
              } else {
                setNativeDatePickerShow(true);
              }
            }}
          />
        }
      />
      {Platform.OS === 'web' ? (
        <Modal
          animationType="fade"
          transparent
          visible={modalVisible}
          onRequestClose={() => setModalVisible(false)}
        >
          <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
            <View style={styles.modalOverlay}>
              <TouchableWithoutFeedback>
                <View style={styles.calendarContainer}>
                  <DateTimePicker
                    mode="single"
                    date={dateValue}
                    onChange={({ date }) => {
                      let dateString = '';
                      if (date instanceof Date) {
                        dateString = formatDateToString(date);
                      } else if (typeof date === 'number') {
                        dateString = formatDateToString(new Date(date));
                      } else if (typeof date === 'string') {
                        dateString = date;
                      }
                      onChange(dateString);
                      setModalVisible(false);
                    }}
                    maxDate={maxDate ? new Date(maxDate) : undefined}
                    style={{ backgroundColor: isDarkMode ? '#333' : '#fff' }}
                    styles={themedStyles}
                    locale="en"
                  />
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      ) : (
        nativeDatePickerShow && (
          <NativeDateTimePicker
            value={dateValue || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={onNativeDateChange}
            maximumDate={maxDate ? new Date(maxDate) : undefined}
            accentColor={theme.colors.primary}
            themeVariant={isDarkMode ? 'dark' : 'light'}
            textColor={isDarkMode ? '#FFFFFF' : '#000000'}
            style={{ backgroundColor: Platform.OS === 'android' ? androidBackgroundColor : undefined }}
            {...(Platform.OS === 'android'
              ? {
                  theme: {
                    backgroundColor: androidBackgroundColor,
                    headerBackgroundColor: androidHeaderBackgroundColor,
                    headerTextColor: androidHeaderTextColor,
                    textColor: androidTextColor,
                    accentColor: androidAccentColor,
                    calendarTextColor: androidTextColor,
                    calendarBackgroundColor: androidBackgroundColor,
                    dayColor: androidTextColor,
                    selectedDayColor: '#FFFFFF',
                    selectedDayBackgroundColor: androidAccentColor
                  }
                }
              : {})}
          />
        )
      )}
    </>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  calendarContainer: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 16,
    width: '90%',
    maxWidth: 400,
    maxHeight: screenWidth <= 480 ? '80%' : '70%',
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      alignSelf: 'center',
    } : {}),
  }
}); 